import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "Enter a valid email";
    if (!form.message.trim()) e.message = "Message is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      toast.success("Message received. We'll get back to you shortly.");
      setForm({ name: "", email: "", message: "" });
      setErrors({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Unable to submit right now. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onChange = (k) => (ev) => {
    setForm((f) => ({ ...f, [k]: ev.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-36 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-10 md:gap-16">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-500">
                05 / Contact
              </span>
              <span className="w-20 h-px bg-white/10" />
            </div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-[56px] tracking-tighter font-medium text-white leading-[1.02]">
              Start a conversation.
            </h2>
            <p className="mt-6 max-w-md text-[15px] md:text-base text-zinc-400 font-light leading-relaxed">
              For research collaboration, technical inquiries, or general
              correspondence, reach us directly or send a message. We respond
              thoughtfully.
            </p>

            <div className="mt-12 space-y-8">
              <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-2">
                  Email
                </div>
                <a
                  href="mailto:info@noetonlabsinc.ca"
                  data-testid="contact-email-link"
                  className="font-heading text-xl md:text-2xl tracking-tight text-white hover:text-zinc-300 transition-colors"
                >
                  info@noetonlabsinc.ca
                </a>
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-2">
                  Entity
                </div>
                <div className="font-heading text-xl md:text-2xl tracking-tight text-white">
                  Noeton Labs Inc.
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-500 mb-2">
                  Location
                </div>
                <div className="font-heading text-xl md:text-2xl tracking-tight text-white">
                  Canada
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              noValidate
              className="border border-white/10 p-8 md:p-12 bg-[#070707]"
            >
              <div className="flex items-center justify-between mb-10">
                <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-500">
                  Form / Message
                </span>
                <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-600">
                  v1.0
                </span>
              </div>

              <div className="space-y-10">
                <Field
                  id="name"
                  label="Your Name"
                  index="01"
                  error={errors.name}
                >
                  <input
                    id="name"
                    data-testid="contact-input-name"
                    type="text"
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Full name"
                    className="w-full bg-transparent border-0 border-b border-white/15 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors font-body text-base"
                  />
                </Field>
                <Field
                  id="email"
                  label="Email Address"
                  index="02"
                  error={errors.email}
                >
                  <input
                    id="email"
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="you@domain.com"
                    className="w-full bg-transparent border-0 border-b border-white/15 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors font-body text-base"
                  />
                </Field>
                <Field
                  id="message"
                  label="Message"
                  index="03"
                  error={errors.message}
                >
                  <textarea
                    id="message"
                    data-testid="contact-input-message"
                    rows={5}
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder="Briefly describe the subject of your message…"
                    className="w-full bg-transparent border-0 border-b border-white/15 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors font-body text-base resize-none"
                  />
                </Field>
              </div>

              <div className="mt-12 flex items-center justify-between gap-4 flex-wrap">
                <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-500">
                  END OF TRANSMISSION
                </span>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={submitting}
                  className="group inline-flex items-center gap-3 bg-white text-black px-7 py-4 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-zinc-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      Sending
                      <Loader2 size={14} className="animate-spin" />
                    </>
                  ) : (
                    <>
                      Send Message
                      <ArrowRight
                        size={14}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, index, error, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label
          htmlFor={id}
          className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-400"
        >
          {label}
        </label>
        <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-600">
          {index}
        </span>
      </div>
      {children}
      {error && (
        <div
          data-testid={`contact-error-${id}`}
          className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase text-red-400"
        >
          {error}
        </div>
      )}
    </div>
  );
}
